// Global Instructions Rule Applied!
import React, { useState, useEffect } from 'react'
import { Modal, Space, message, Skeleton, Divider } from 'antd'
import Button from '../../../core/components/Button.jsx'
import { InputManager } from '../../../core/components/InputManager/InputManager.tsx'
import { FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import { InteractionRecordFields } from '../../../state_manager/data_models/interaction_record.ts'
import { fetchInteractionLinks, processInteractionLinks } from '../services/interactionsService.ts'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export function LinkDelinkModal({ visible, interaction, onCancel, onSuccess, accessToken, triggerRef }) {
  const [loading, setLoading] = useState(false)
  const [loadingLinks, setLoadingLinks] = useState(false)
  const [modalStyle, setModalStyle] = useState({})
  const [fieldValues, setFieldValues] = useState({
    account_ids: [],
    deal_ids: [],
    prospect_ids: []
  })

  useEffect(() => {
    if (visible && interaction) {
      loadExistingLinks()
      updateModalPosition()

      // Update position on scroll and resize
      const handleUpdatePosition = () => {
        if (visible) {
          updateModalPosition()
        }
      }

      window.addEventListener('scroll', handleUpdatePosition, true)
      window.addEventListener('resize', handleUpdatePosition)

      return () => {
        window.removeEventListener('scroll', handleUpdatePosition, true)
        window.removeEventListener('resize', handleUpdatePosition)
      }
    } else if (!visible) {
      // Only reset field values when closing, but keep modalStyle until afterClose
      setFieldValues({
        account_ids: [],
        deal_ids: [],
        prospect_ids: []
      })
    }
  }, [visible, interaction])

  const handleAfterClose = () => {
    // Reset modal style only after the close animation completes
    setModalStyle({})
  }

  const updateModalPosition = () => {
    if (!triggerRef?.current) {
      // Fallback to centered if no trigger ref provided
      setModalStyle({})
      return
    }

    const triggerElement = triggerRef.current
    const rect = triggerElement.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    // Position modal below and to the right of the trigger, similar to Popconfirm
    const modalWidth = 600
    const modalHeight = 400 // Approximate height
    const gap = 8

    let top = rect.bottom + scrollTop + gap
    let left = rect.left + scrollLeft

    // Ensure modal doesn't go off-screen horizontally
    const windowWidth = window.innerWidth
    if (left + modalWidth > windowWidth) {
      left = windowWidth - modalWidth - 16
    }
    if (left < 16) {
      left = 16
    }

    // Ensure modal doesn't go off-screen vertically
    const windowHeight = window.innerHeight
    const availableSpaceBelow = windowHeight - rect.bottom
    const availableSpaceAbove = rect.top

    if (availableSpaceBelow < modalHeight && availableSpaceAbove > modalHeight) {
      // Position above if there's more space above
      top = rect.top + scrollTop - modalHeight - gap
    } else if (availableSpaceBelow < modalHeight) {
      // If not enough space below, position at bottom of viewport
      top = scrollTop + windowHeight - modalHeight - 16
    }

    setModalStyle({
      top: `${top}px`,
      left: `${left}px`,
      paddingBottom: 0,
      margin: 0,
      maxWidth: `${modalWidth}px`
    })
  }

  const loadExistingLinks = async () => {
    if (!interaction || !accessToken) return

    const interactionId = interaction[InteractionRecordFields.interactions__id]
    if (!interactionId) return

    try {
      setLoadingLinks(true)
      const links = await fetchInteractionLinks(interactionId, accessToken)

      const accountIds = (links.account_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))
      const dealIds = (links.deal_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))
      const prospectIds = (links.prospect_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))

      setFieldValues({
        account_ids: accountIds,
        deal_ids: dealIds,
        prospect_ids: prospectIds
      })
    } catch (error) {
      console.error('Error loading interaction links:', error)
      message.error('Failed to load existing links')
    } finally {
      setLoadingLinks(false)
    }
  }

  const handleSave = async () => {
    try {
      const interactionId = interaction[InteractionRecordFields.interactions__id]

      if (!interactionId) {
        message.error('Interaction ID not found')
        return
      }

      setLoading(true)

      const accountIds = (fieldValues.account_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })
      const dealIds = (fieldValues.deal_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })
      const prospectIds = (fieldValues.prospect_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })

      await processInteractionLinks(
        {
          interaction_id: interactionId,
          account_ids: accountIds,
          deal_ids: dealIds,
          prospect_ids: prospectIds
        },
        accessToken
      )

      message.success('Links updated successfully')
      onSuccess?.()
      onCancel()
    } catch (error) {
      console.error('Error saving interaction links:', error)
      message.error(error.message || 'Failed to save links')
    } finally {
      setLoading(false)
    }
  }

  const interactionName = interaction?.[InteractionRecordFields.interactions__subject] || 'Interaction'

  const modalTitle = (
    <span>
      Link/Delink: <span style={{ color: '#000000', fontWeight: 'bold' }}>{interactionName}</span>
    </span>
  )

  const fields = [
    {
      key: 'account_ids',
      label: 'Accounts',
      type: FieldTypeEnum.COMPANY,
      value: fieldValues.account_ids,
      mode: 'multiple',
      placeholder: 'Search accounts...',
      required: false,
      autoFocus: true
    },
    {
      key: 'deal_ids',
      label: 'Deals',
      type: FieldTypeEnum.DEAL,
      value: fieldValues.deal_ids,
      mode: 'multiple',
      placeholder: 'Search deals...',
      required: false
    },
    {
      key: 'prospect_ids',
      label: 'Prospects',
      type: FieldTypeEnum.PROSPECT,
      value: fieldValues.prospect_ids,
      mode: 'multiple',
      placeholder: 'Search prospects...',
      required: false
    }
  ]

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      closable={false}
      maskClosable={false}
      footer={
        <Space>
          <Button variant='cancel' onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
            variant='save'
            onClick={handleSave}
            loading={loading}
          >
            Save
          </Button>
        </Space>
      }
      width={600}
      style={Object.keys(modalStyle).length > 0 ? modalStyle : undefined}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }
      }}
    >
      <Divider style={{ margin: '16px 0' }} />
      {loadingLinks ? (
        <div className='flex flex-col gap-4'>
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
        </div>
      ) : (
        <InputManager
          inputMode='edit'
          fields={fields}
          labelPosition='above'
          onChange={(key, value) => {
            setFieldValues((prev) => ({
              ...prev,
              [key]: value
            }))
          }}
        />
      )}
    </Modal>
  )
}
